import Immutable from 'immutable';
import { AppState, AppStateActionParams } from 'records/AppState';
import { NewProject } from 'records/NewProject';
import { Project, ProjectList, ProjectMap } from 'records/Project';
import { PunchQuery } from 'records/PunchQuery';
import { RFIQuery } from 'records/RFIQuery';
import { SubmittalListTypes } from 'records/Submittal';
import { UpdateProject } from 'records/UpdateProject';
import { geocode } from 'services/mapService';
import {
  createProjectBasedTrial,
  createProjectWithSubscription,
  getProject,
  getProjects,
  updateProject,
} from 'services/projectService';
import LStorage from 'utils/LStorage';

export interface ProjectActions {
  getProject: (
    actionParams: AppStateActionParams,
    projectId: string
  ) => Promise<AppState>;

  getProjects: (
    actionParams: AppStateActionParams,
    clear: boolean
  ) => Promise<AppState>;

  setProjectLocation: (
    actionParams: AppStateActionParams,
    projectId: string
  ) => Promise<AppState>;

  setSelectedProject: (
    actionParams: AppStateActionParams,
    selectedProjectId: string
  ) => AppState;

  loadProject: (
    actionParams: AppStateActionParams,
    selectedProjectId: string
  ) => IterableIterator<AppState | Promise<AppState>>;

  createProject: (
    actionParams: AppStateActionParams,
    newProject: NewProject,
    isProjectBasedTrial: boolean,
    callback: (project: Project) => void
  ) => Promise<AppState>;

  updateProject: (
    actionParams: AppStateActionParams,
    projectName: UpdateProject
  ) => Promise<AppState>;
}

const projectActions = {
  getProjects: async (
    { state }: AppStateActionParams,
    clear = false
  ): Promise<AppState> => {
    const { userLicense } = state();
    const projects: ProjectList = await getProjects(userLicense.package);

    const projectsByKey = projects.reduce(
      (acc: ProjectMap, project: Project): ProjectMap =>
        acc.set(project.id, project),
      Immutable.OrderedMap()
    );

    return clear
      ? state().set('projects', projectsByKey)
      : state().mergeIn(['projects'], projectsByKey);
  },

  getProject: async (
    { state }: AppStateActionParams,
    projectId: string
  ): Promise<AppState> => {
    const { userLicense } = state();
    const project = await getProject(projectId, userLicense.package);

    return state().setIn(['projects', project.id], project);
  },

  setProjectLocation: async (
    { state: appState }: AppStateActionParams,
    projectId: string
  ): Promise<AppState> => {
    const { projects } = appState();
    const project = projects.get(projectId);
    if (project) {
      const { street, city, state, zipcode } = project;
      const location = await geocode({ street, city, state, zipcode });
      return appState().setIn(['projects', projectId, 'location'], location);
    }
    return appState();
  },

  setSelectedProject: (
    { state }: AppStateActionParams,
    selectedProjectId: string
  ): AppState => {
    LStorage.set('selected-project', selectedProjectId);

    return state().set('selectedProject', selectedProjectId);
  },

  loadProject: function* loadProject(
    { state, actions }: AppStateActionParams,
    selectedProjectId: string
  ): IterableIterator<AppState | Promise<AppState>> {
    LStorage.set('selected-project', selectedProjectId);

    yield state().set('selectedProject', selectedProjectId);

    yield (async (): Promise<AppState> => {
      const { projects } = state();
      const project = projects.get(selectedProjectId);

      if (project) {
        await actions.clearPages();
        await actions.clearDocuments();
        await actions.clearProcessingStatuses();
        await actions.getRoles();

        await Promise.all([
          actions.getPunches(selectedProjectId),
          actions.getRFIs(),
          actions.getUsers(),
          actions.getSheets(),
          actions.getMappedSheetVersions(),
          actions.getSubmittals(SubmittalListTypes.ALL),
          actions.getRecentUploads(),
        ]);

        await actions.setPunchQuery(new PunchQuery());

        const rfiQuery = new RFIQuery();
        await actions.setRFIQuery(rfiQuery);
      }

      return state();
    })();
  },

  createProject: async (
    { state, actions }: AppStateActionParams,
    newProject: NewProject,
    isProjectBasedTrial: boolean,
    callback: (project: Project) => void
  ): Promise<AppState> => {
    const { id } = await (isProjectBasedTrial
      ? createProjectBasedTrial(newProject)
      : createProjectWithSubscription(newProject));
    const { userLicense } = state();
    const createdProject = await getProject(id, userLicense.package);

    callback(createdProject);

    return state().update(
      'projects',
      (projects: ProjectMap): ProjectMap =>
        projects.set(createdProject.id, createdProject)
    );
  },

  updateProject: async (
    { state }: AppStateActionParams,
    updateProjectData: UpdateProject
  ): Promise<AppState> => {
    const updatedProject = await updateProject(updateProjectData);

    return state().update(
      'projects',
      (projects: ProjectMap): ProjectMap =>
        projects.set(updatedProject.id, updatedProject)
    );
  },
};

export default projectActions;
